import { useTranslation } from "react-i18next"

import type {
  FeasibilityValue,
  ImplTime,
  StatusIndex,
  TimeSavedUnit,
  ToggleValue,
} from "../../types/use-case.types"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Slider } from "@/components/ui/slider"
import { cn } from "@/lib/utils"

import { FEASIBILITY_MAP, IMPL_TIME_OPTIONS, STATUSES } from "../../types/use-case.types"

type FactSheetSidebarFieldsProps = {
  implTime: ImplTime
  feasV: FeasibilityValue
  statIdx: StatusIndex
  users: string
  timeSavedHours: string
  timeSavedUnit: TimeSavedUnit
  togTS: ToggleValue
  togQI: ToggleValue
  togRD: ToggleValue
  togWW: ToggleValue
  onChange: (field: string, value: string | number) => void
}

const STATUS_BADGE_CLASSES: Record<number, string> = {
  0: "bg-success text-success-foreground border border-foreground",
  1: "bg-warning text-warning-foreground border border-foreground",
  2: "bg-muted text-foreground border border-foreground",
  3: "bg-destructive text-destructive-foreground border border-foreground",
}

function ToggleTile({
  label,
  value,
  onClick,
}: {
  label: string
  value: ToggleValue
  onClick: () => void
}) {
  const isYes = value === "yes"
  return (
    <button
      type="button"
      className={cn(
        "focus-visible:ring-ring flex w-full cursor-pointer items-center justify-between rounded-lg border-[1.5px] px-3 py-2 text-left shadow-sm transition-colors select-none focus-visible:ring-2 focus-visible:outline-none",
        isYes ? "border-success bg-success/10" : "border-border bg-muted/50"
      )}
      onClick={onClick}
    >
      <span className="text-muted-foreground text-xs font-medium">{label}</span>
      <span
        className={cn("text-sm font-extrabold", isYes ? "text-success" : "text-muted-foreground")}
      >
        {isYes ? "Yes" : "No"}
      </span>
    </button>
  )
}

export function FactSheetSidebarFields({
  implTime,
  feasV,
  statIdx,
  users,
  timeSavedHours,
  timeSavedUnit,
  togTS,
  togQI,
  togRD,
  togWW,
  onChange,
}: FactSheetSidebarFieldsProps) {
  const { t } = useTranslation()

  const feasConfig = FEASIBILITY_MAP[feasV]
  const statusConfig = STATUSES[statIdx]

  const unitLabelMap: Record<TimeSavedUnit, string> = {
    week: "h/week",
    month: "h/month",
    once: "h (one-time)",
  }
  const unitLabel = unitLabelMap[timeSavedUnit]

  const cycleStatus = () => {
    onChange("statIdx", (statIdx + 1) % STATUSES.length)
  }

  const toggleField = (field: string, current: ToggleValue) => {
    onChange(field, current === "yes" ? "no" : "yes")
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Implementation Time */}
      <div className="bg-card rounded-lg border p-3 shadow-sm">
        <label className="text-muted-foreground mb-1 block text-xs font-semibold tracking-wide uppercase">
          {t("useCases.factSheet.sidebar.implTime")}
        </label>
        <Select
          value={implTime || "__empty__"}
          onValueChange={(v) => onChange("implTime", v === "__empty__" ? "" : v)}
        >
          <SelectTrigger>
            <SelectValue placeholder={t("useCases.factSheet.sidebar.selectTimeframe")} />
          </SelectTrigger>
          <SelectContent>
            {IMPL_TIME_OPTIONS.map((opt) => (
              <SelectItem key={opt.value || "__empty__"} value={opt.value || "__empty__"}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Feasibility */}
      <div className="bg-card rounded-lg border p-3 shadow-sm">
        <label className="text-muted-foreground mb-1 block text-xs font-semibold tracking-wide uppercase">
          {t("useCases.factSheet.sidebar.feasibility")}
        </label>
        <div className="mt-2">
          <Slider
            max={3}
            min={1}
            step={1}
            value={[parseInt(feasV)]}
            onValueChange={(v) => onChange("feasV", String(v[0]))}
          />
          <div className="text-muted-foreground mt-2 flex justify-between text-[10px]">
            <span>{t("useCases.factSheet.sidebar.lowComplex")}</span>
            <span>{t("useCases.factSheet.sidebar.medium")}</span>
            <span>{t("useCases.factSheet.sidebar.highEasy")}</span>
          </div>
          <div className="mt-1 text-xs font-bold" style={{ color: feasConfig.color }}>
            {feasConfig.label}
          </div>
        </div>
      </div>

      {/* Status */}
      <div className="bg-card rounded-lg border p-3 shadow-sm">
        <label className="text-muted-foreground mb-1 block text-xs font-semibold tracking-wide uppercase">
          {t("useCases.factSheet.sidebar.status")}
        </label>
        <button
          title="Click to cycle"
          type="button"
          className={cn(
            "focus-visible:ring-ring inline-block cursor-pointer rounded-full border-[1.5px] px-3 py-0.5 text-left text-xs font-bold transition-colors select-none focus-visible:ring-2 focus-visible:outline-none",
            STATUS_BADGE_CLASSES[statIdx]
          )}
          onClick={cycleStatus}
        >
          {statusConfig.text}
        </button>
      </div>

      {/* Affected Persons */}
      <div className="bg-card rounded-lg border p-3 shadow-sm">
        <label className="text-muted-foreground mb-1 block text-xs font-semibold tracking-wide uppercase">
          {t("useCases.factSheet.sidebar.affectedPersons")}
        </label>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-xs">
            {t("useCases.factSheet.sidebar.activeUsers")}
          </span>
          <span className="text-sm font-extrabold tabular-nums">{users || "0"}</span>
        </div>
      </div>

      {/* Time Saved / Person */}
      <div className="bg-card rounded-lg border p-3 shadow-sm">
        <label className="text-muted-foreground mb-1 block text-xs font-semibold tracking-wide uppercase">
          {t("useCases.factSheet.sidebar.timeSaved")}
        </label>
        <div className="text-sm font-extrabold">
          {timeSavedHours || "0"} {unitLabel}
        </div>
      </div>

      {/* Toggle Tiles */}
      <ToggleTile
        label={t("useCases.factSheet.sidebar.timeSavings")}
        value={togTS}
        onClick={() => toggleField("togTS", togTS)}
      />
      <ToggleTile
        label={t("useCases.factSheet.sidebar.qualityImprovement")}
        value={togQI}
        onClick={() => toggleField("togQI", togQI)}
      />
      <ToggleTile
        label={t("useCases.factSheet.sidebar.rdCapabilities")}
        value={togRD}
        onClick={() => toggleField("togRD", togRD)}
      />
      <ToggleTile
        label={t("useCases.factSheet.sidebar.improvedWow")}
        value={togWW}
        onClick={() => toggleField("togWW", togWW)}
      />
    </div>
  )
}
